/**
 * Settings › General: the RudderStack workspaces that Lookout can read (ACT2-1124 LLD §9, D18). Agents read these
 * workspaces, and the account picker copies only their accounts. The list starts empty.
 *
 * - Only the Lookout owner or a Lookout admin who is also a RudderStack admin adds or removes a workspace. Everyone
 *   else sees the list with the controls disabled, and the reason.
 * - The add dialog lists the organization's workspaces of the same region. The server lists first the workspaces that
 *   load a warehouse this workspace uses (the warehouse match). Nothing is added by itself.
 * - A removal stops new reads and copies. Accounts that are already connected or copied stay, and the dialog says so.
 */
import type { RouterClient } from '@orpc/server';
import type { AppRouter } from '@server/rpc';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Plus, Waypoints } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { ActionTooltip } from '@/_shared/components/ActionTooltip';
import { Table, type TableColumn } from '@/_shared/components/Table';
import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/_shared/components/ui/dialog';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/_shared/components/ui/empty';
import { Skeleton } from '@/_shared/components/ui/skeleton';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { orpcErrorMessage } from '@/_shared/lib/orpc-errors';

import { SettingsSectionHeader } from '../SettingsSectionHeader';

type ReadableScreen = Awaited<ReturnType<RouterClient<AppRouter>['readableWorkspaces']['list']>>;
type SiblingReadableScreen = Extract<ReadableScreen, { sibling: true }>;
type ReadableRow = SiblingReadableScreen['workspaces'][number];
type CandidateRow = SiblingReadableScreen['candidates'][number];

export const SECTION_TITLE = 'RudderStack workspaces Lookout can read';
export const NOT_EDITOR_REASON =
  'Only a Lookout admin who is also a RudderStack admin of the organization can change this list.';

function isSiblingScreen(data: ReadableScreen | undefined): data is SiblingReadableScreen {
  return data?.sibling === true;
}

export function ReadableWorkspacesSection(): React.ReactElement {
  const orpc = useOrpc();
  const queryClient = useQueryClient();
  const listQuery = useQuery(orpc.readableWorkspaces.list.queryOptions({ input: {} }));
  const [addOpen, setAddOpen] = useState(false);
  const [addingId, setAddingId] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<ReadableRow | null>(null);

  // The list changes what the account picker and the Warehouses tab offer.
  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: orpc.readableWorkspaces.key() });
    void queryClient.invalidateQueries({ queryKey: orpc.accountCopies.key() });
  };
  const add = useMutation(
    orpc.readableWorkspaces.add.mutationOptions({
      onSuccess: invalidate,
      onError: (err) => toast.error(orpcErrorMessage(err, 'Could not add the workspace')),
      onSettled: () => setAddingId(null),
    }),
  );
  const remove = useMutation(
    orpc.readableWorkspaces.remove.mutationOptions({
      onSuccess: () => {
        invalidate();
        setRemoveTarget(null);
      },
      onError: (err) => toast.error(orpcErrorMessage(err, 'Could not remove the workspace')),
    }),
  );

  const screen = isSiblingScreen(listQuery.data) ? listQuery.data : null;
  const canEdit = screen?.canEdit ?? false;

  const header = (
    <SettingsSectionHeader
      title={SECTION_TITLE}
      description="Agents read these workspaces, and you can use their warehouse and destination accounts. Agents always read this Lookout workspace's own RudderStack workspace."
      action={
        screen ? (
          <ActionTooltip show={!canEdit} message={NOT_EDITOR_REASON}>
            <Button
              type="button"
              variant="secondary"
              Icon={<Plus />}
              label="Add workspace"
              disabled={!canEdit}
              onClick={() => setAddOpen(true)}
            />
          </ActionTooltip>
        ) : null
      }
    />
  );

  if (listQuery.isPending) {
    return (
      <section className="mb-8" aria-label={SECTION_TITLE}>
        {header}
        <div className="flex flex-col gap-2" role="status" aria-label="Loading the readable workspaces">
          <Skeleton className="h-12 w-full rounded-md" />
          <Skeleton className="h-12 w-full rounded-md" />
        </div>
      </section>
    );
  }

  if (listQuery.isError) {
    return (
      <section className="mb-8" aria-label={SECTION_TITLE}>
        {header}
        <Callout variant="error">
          {orpcErrorMessage(listQuery.error, 'Could not load the workspaces Lookout can read.')}
        </Callout>
      </section>
    );
  }

  // Not a sibling workspace: it has no readable workspaces.
  if (!screen) return <></>;

  const columns: TableColumn<ReadableRow>[] = [
    {
      id: 'workspace',
      header: 'Workspace',
      cell: (row) => <span className="block truncate text-sm font-medium text-foreground">{row.name}</span>,
    },
    {
      id: 'added',
      header: 'Added',
      width: '9rem',
      cell: (row) => <span className="text-sm">{dayjs(row.addedAt).format('MMM D, YYYY')}</span>,
    },
    {
      id: 'action',
      header: '',
      headerAriaLabel: 'Actions',
      width: '8rem',
      align: 'right',
      sortable: false,
      cell: (row) => (
        <ActionTooltip show={!canEdit} message={NOT_EDITOR_REASON}>
          <Button
            type="button"
            size="sm"
            variant="secondary-destructive"
            label="Remove"
            aria-label={`Remove ${row.name}`}
            disabled={!canEdit}
            onClick={() => setRemoveTarget(row)}
          />
        </ActionTooltip>
      ),
    },
  ];

  // The add dialog lists the candidates in the same table as the section.
  const candidateColumns: TableColumn<CandidateRow>[] = [
    {
      id: 'workspace',
      header: 'Workspace',
      cell: (candidate) => <span className="block truncate text-sm font-medium text-foreground">{candidate.name}</span>,
    },
    {
      id: 'action',
      header: '',
      headerAriaLabel: 'Actions',
      width: '6rem',
      align: 'right',
      sortable: false,
      cell: (candidate) => (
        <Button
          type="button"
          size="sm"
          variant="secondary"
          label="Add"
          aria-label={`Add ${candidate.name}`}
          loading={add.isPending && addingId === candidate.rudderstackWorkspaceId}
          disabled={add.isPending && addingId !== candidate.rudderstackWorkspaceId}
          onClick={() => {
            setAddingId(candidate.rudderstackWorkspaceId);
            add.mutate({ rudderstackWorkspaceId: candidate.rudderstackWorkspaceId });
          }}
        />
      ),
    },
  ];

  return (
    <section className="mb-8" aria-label={SECTION_TITLE}>
      {header}
      {!screen.namesAvailable ? (
        <Callout variant="warning" className="mb-4">
          Lookout could not read the workspace names from RudderStack, so some workspaces show without a name.
        </Callout>
      ) : null}
      {screen.workspaces.length === 0 ? (
        <Empty className="border">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Waypoints />
            </EmptyMedia>
            <EmptyTitle>No workspaces yet</EmptyTitle>
            <EmptyDescription>
              Agents read only this workspace&apos;s own RudderStack workspace. Add a workspace so that agents can read
              it and you can use its accounts.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <Table data={screen.workspaces} columns={columns} getRowId={(row) => row.rudderstackWorkspaceId} fixedLayout />
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add a workspace</DialogTitle>
            <DialogDescription>
              Agents can then read the workspace, and you can use its accounts. The list shows the workspaces of this
              organization in the {screen.region ?? 'same'} region.
            </DialogDescription>
          </DialogHeader>
          {screen.candidates.length === 0 ? (
            <p className="text-sm text-muted-foreground">No other workspace of this region to add.</p>
          ) : (
            <Table
              data={screen.candidates}
              columns={candidateColumns}
              getRowId={(candidate) => candidate.rudderstackWorkspaceId}
              fixedLayout
              maxHeight="18rem"
            />
          )}
          <DialogFooter>
            <Button type="button" variant="secondary" label="Done" onClick={() => setAddOpen(false)} />
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={removeTarget !== null}
        onOpenChange={(open) => {
          if (!open && !remove.isPending) setRemoveTarget(null);
        }}
      >
        <DialogContent
          onEscapeKeyDown={(event) => {
            if (remove.isPending) event.preventDefault();
          }}
          onInteractOutside={(event) => {
            if (remove.isPending) event.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle>Remove {removeTarget?.name ?? 'the workspace'}?</DialogTitle>
            <DialogDescription>
              Agents stop reading this workspace, and Lookout makes no new copies of its accounts. Accounts that you
              already connected or copied stay in this workspace.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              label="Cancel"
              disabled={remove.isPending}
              onClick={() => setRemoveTarget(null)}
            />
            <Button
              type="button"
              variant="primary-destructive"
              label="Remove"
              loading={remove.isPending}
              onClick={() => {
                if (removeTarget) remove.mutate({ rudderstackWorkspaceId: removeTarget.rudderstackWorkspaceId });
              }}
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
